import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Snackbar,
  Alert,
  Chip,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  styled,
  CircularProgress,
} from "@mui/material";
import {
  QrCodeScanner as QrCodeScannerIcon,
  Cameraswitch as CameraswitchIcon,
  FlashOn as FlashOnIcon,
  FlashOff as FlashOffIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import {
  UPLOAD_SCAN_RESULT,
  CHECK_DUPLICATE_ID,
  GET_MY_AUTH,
  GET_ROLES,
  CHECK_COUPON_CLAIMED,
  SEND_COUPON_EMAIL,
} from "../graphql/queries";
import { useNavigate } from "react-router-dom";
import QrReader from "react-qr-reader";
import { timestampToDate } from "../utils/dateUtils";
import CouponFormDialog from "../Components/CouponFormDialog";
import { COUPON_CONFIG } from "../constants/couponConstants";
import { parseServerDate } from "../utils/serverDate";

// Styled components
const ScannerContainer = styled(Paper)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[8],
  height: "100%",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: {
    height: "calc(100vh - 240px)",
    maxHeight: "70vh",
  },
}));

const ScannerViewport = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  position: "relative",
  overflow: "hidden",
  borderRadius: `${theme.shape.borderRadius * 2}px ${
    theme.shape.borderRadius * 2
  }px 0 0`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#000",

  [theme.breakpoints.down("md")]: {
    height: "100%",
    width: "100%",
  },

  "& video": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
  },

  "& > div": {
    width: "100% !important",
    height: "100% !important",
    maxWidth: "100% !important",
    maxHeight: "100% !important",
    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",
  },

  "& > div > section": {
    width: "100% !important",
    height: "100% !important",
    padding: "0 !important",
  },
}));

const ScannerControls = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: theme.spacing(1),
  right: theme.spacing(1),
  display: "flex",
  gap: theme.spacing(1),
  zIndex: 10,
}));

const ScannerOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 5,
  pointerEvents: "none",
}));

const ScanFrame = styled(Box)(({ theme, success }) => ({
  width: "70%",
  height: "70%",
  maxWidth: 280,
  maxHeight: 280,
  border: "none",
  borderRadius: theme.shape.borderRadius,
  boxShadow: "none",
  position: "relative",

  [theme.breakpoints.down("md")]: {
    width: "80%",
    height: "60%",
    maxWidth: "none",
    maxHeight: "none",
  },

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    border: "none",
    borderRadius: theme.shape.borderRadius - 1,
  },
}));

const ScannerHeader = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 10,
  padding: theme.spacing(2),
  background:
    "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const ScannerFooter = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

// Simple placeholder for QR scanner if react-qr-reader is not installed
const QrReaderPlaceholder = ({ onResult }) => {
  const handleSimulatedScan = () => {
    // Simulate a scan after 2 seconds when the user clicks on the placeholder
    setTimeout(() => {
      onResult({ text: "1234567890" });
    }, 2000);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        bgcolor: "black",
        color: "white",
        position: "relative",
      }}
      onClick={handleSimulatedScan}
    >
      <QrCodeScannerIcon sx={{ fontSize: 80, mb: 2, opacity: 0.8 }} />
      <Typography variant="h6" gutterBottom>
        QR Scanner Placeholder
      </Typography>
      <Typography
        variant="body2"
        align="center"
        sx={{ maxWidth: "80%", opacity: 0.7 }}
      >
        Click here to simulate a scan
      </Typography>
      <Typography variant="caption" sx={{ mt: 2, opacity: 0.5 }}>
        To use a real scanner, install react-qr-reader:
      </Typography>
      <Typography
        variant="caption"
        sx={{ opacity: 0.5, fontFamily: "monospace" }}
      >
        npm install react-qr-reader
      </Typography>
    </Box>
  );
};

const CameraPermissionDenied = ({ setHasPermission }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      bgcolor: "background.paper",
      p: 3,
      textAlign: "center",
    }}
  >
    <QrCodeScannerIcon sx={{ fontSize: 60, mb: 2, color: "error.main" }} />
    <Typography variant="h6" gutterBottom color="error">
      Camera Access Denied
    </Typography>
    <Typography variant="body2" sx={{ mb: 3 }}>
      Please enable camera access in your browser settings to use the scanner.
    </Typography>
    <Button
      variant="contained"
      color="primary"
      onClick={() => {
        navigator.mediaDevices
          .getUserMedia({ video: true })
          .then(() => setHasPermission(true))
          .catch((err) => console.error("Error requesting permission:", err));
      }}
    >
      Request Permission Again
    </Button>
  </Box>
);

// Helper function to format role for display
const formatRoleForDisplay = (role) => {
  if (!role) return "";

  // Split by underscore or hyphen
  return role
    .split(/[_-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Function to calculate time difference
const getTimeDifference = (timestamp) => {
  if (!timestamp) return "Unknown time";

  console.log("Processing timestamp:", timestamp);

  const now = new Date();
  let then;

  try {
    // Handle both Unix timestamps and ISO strings
    if (typeof timestamp === "number" || /^\d+$/.test(timestamp)) {
      then = timestampToDate(timestamp);
    } else {
      /*
       * A NAIVE DB DATETIME IS THE SERVER'S WALL CLOCK, NOT THE BROWSER'S.
       *
       * `new Date("2026-10-12 01:51:05")` reads those digits as the VIEWER's local time. The
       * database wrote them in the SERVER's zone (Europe/Berlin), so the gap between the two
       * zones becomes the error — from New Delhi, 5:30 − 2:00 = 3h30m, which is why a scan made
       * seconds earlier read "3 hours ago".
       *
       * parseServerDate leaves anything that already carries a zone (an ISO `Z`, an offset, an
       * epoch) exactly as it is, so only the naive strings are corrected.
       */
      then = parseServerDate(timestamp) || new Date(timestamp);
    }

    if (!then || isNaN(then.getTime())) {
      console.error("Invalid date from timestamp:", timestamp);
      return "Invalid date";
    }

    console.log("Parsed date:", then);

    // Check if date is in the future
    if (then > now) {
      return "Future date";
    }

    const diffMs = now - then;
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    return "Just now";
  } catch (error) {
    console.error("Error calculating time difference:", error, timestamp);
    return "Date error";
  }
};

const ScanningVisitorsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [scanning, setScanning] = useState(true);
  const [scannedData, setScannedData] = useState(null);
  const [scannedRole, setScannedRole] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [manualDialog, setManualDialog] = useState(false);
  const [manualId, setManualId] = useState("");
  const [manualRole, setManualRole] = useState("");
  const [leadType, setLeadType] = useState("Hot Lead");
  const [remark, setRemark] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [scanSuccess, setScanSuccess] = useState(false);
  const [leadDialog, setLeadDialog] = useState(false);
  const [recentScans, setRecentScans] = useState([]);
  const [hasPermission, setHasPermission] = useState(false);
  const [originalData, setOriginalData] = useState("");
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [transitioningToForm, setTransitioningToForm] = useState(false);
  const [alreadyClaimedDialog, setAlreadyClaimedDialog] = useState(false);
  const [validityExpiredDialog, setValidityExpiredDialog] = useState(false);

  const isCouponValidityExpired = () =>
    new Date() > new Date(COUPON_CONFIG.VALIDITY_END_DATE);

  // GraphQL Queries & Mutations
  const { data: authData, loading: authLoading } = useQuery(GET_MY_AUTH);
  const {
    data: rolesData,
    loading: rolesLoading,
  } = useQuery(GET_ROLES);
  const [uploadScanResult, { loading: uploadLoading }] =
    useMutation(UPLOAD_SCAN_RESULT);
  const [checkDuplicate] = useMutation(CHECK_DUPLICATE_ID);
  const [checkCouponClaimedQuery] = useLazyQuery(CHECK_COUPON_CLAIMED);
  const [sendCouponEmail] = useMutation(SEND_COUPON_EMAIL);
  const navigate = useNavigate();

  // Process roles data when received
  useEffect(() => {
    setLoadingRoles(rolesLoading);

    if (rolesData?.getRoles && Array.isArray(rolesData.getRoles)) {
      setAvailableRoles(rolesData.getRoles);
    }
  }, [rolesData, rolesLoading]);

  // After QR reader unmounts (transitioningToForm), wait long enough for camera to be fully released before opening form
  useEffect(() => {
    if (!transitioningToForm) return;
    const t = setTimeout(() => {
      setShowCouponForm(true);
      setTransitioningToForm(false);
    }, 800);
    return () => clearTimeout(t);
  }, [transitioningToForm]);

  // Add this useEffect for camera detection
  useEffect(() => {
    const detectCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoCameras = devices.filter(
          (device) => device.kind === "videoinput"
        );

        // Get camera permission
        if (videoCameras.length > 0) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
          });
          setHasPermission(true);
          // Clean up the stream
          stream.getTracks().forEach((track) => track.stop());
        }
      } catch (err) {
        console.error("Error accessing camera:", err);
        setNotification({
          open: true,
          message:
            "Camera access denied. Please check your browser permissions.",
          severity: "error",
        });
      }
    };

    detectCameras();
  }, []);

  // Handle scan result
  const handleScan = (result) => {
    if (!result || !scanning) return;

    // Extract the data from QR scan result
    const rawData = typeof result === "string" ? result : result?.text || "";
    if (!rawData) return;

    // Save the original data for display
    setOriginalData(rawData);

    // Handle MECARD / VCARD: try to extract NOTE field content
    let fullData = rawData;
    const isCardFormat = /^MECARD:|^BEGIN:VCARD/i.test(rawData.trim());
    if (isCardFormat) {
      const noteMatch = rawData.match(/NOTE:([^;]+)/i);
      if (noteMatch && noteMatch[1]) {
        fullData = noteMatch[1].trim();
      }
    }

    // Wait for auth data to load
    if (authLoading) {
      setNotification({
        open: true,
        message: "Please wait while we verify your authentication...",
        severity: "info",
      });
      return;
    }

    // Get the logged-in user's eventId
    const userEventId = authData?.eventId;

    if (!userEventId) {
      setNotification({
        open: true,
        message: "Unable to verify event. Please try logging in again.",
        severity: "error",
      });
      return;
    }

    // STRICT VALIDATION: Check if QR code is a URL or invalid format
    const isUrl = /^https?:\/\//i.test(fullData.trim());
    if (isUrl) {
      setNotification({
        open: true,
        message:
          "Invalid QR code: This appears to be a URL, not a valid badge QR code.",
        severity: "error",
      });
      return;
    }

    // STRICT VALIDATION: QR code MUST be in format: eventId,role,id (exactly 3 parts)
    if (!fullData.includes(",")) {
      setNotification({
        open: true,
        message: "Invalid QR code format: Expected format is 'eventId,role,id'",
        severity: "error",
      });
      return;
    }

    const dataParts = fullData.split(",");

    // Must have exactly 3 parts: eventId, role, id
    if (dataParts.length !== 3) {
      setNotification({
        open: true,
        message: `Invalid QR code format: Expected 3 parts separated by commas (eventId,role,id), but found ${dataParts.length} part(s).`,
        severity: "error",
      });
      return;
    }

    // Extract the parts
    const scannedEventId = dataParts[0].trim();
    const role = dataParts[1].trim();
    const scannedId = dataParts[2].trim();

    // Validate that all parts are not empty
    if (!scannedEventId || !role || !scannedId) {
      setNotification({
        open: true,
        message:
          "Invalid QR code: All parts (eventId, role, id) must be present and non-empty.",
        severity: "error",
      });
      return;
    }

    // CRITICAL: Validate eventId matches the logged-in user's eventId
    if (scannedEventId !== userEventId) {
      setNotification({
        open: true,
        message: "Invalid QR code: This QR code belongs to a different event.",
        severity: "error",
      });
      return;
    }

    // If we reach here, the QR code is valid and eventId matches
    // Show success animation
    setScanSuccess(true);
    setTimeout(() => setScanSuccess(false), 1500);

    // Stop scanning
    setScanning(false);
    setScannedData(scannedId);
    setScannedRole(role);

    // Check if this is the coupon event
    if (userEventId === COUPON_CONFIG.EVENT_ID) {
      if (isCouponValidityExpired()) {
        setScannedData(scannedId);
        setScannedRole(role);
        setValidityExpiredDialog(true);
        return;
      }
      // Check if coupon already claimed
      checkCouponClaimedQuery({
        variables: { id: scannedId, role },
      })
        .then((result) => {
          if (result.data?.checkCouponClaimed) {
            setAlreadyClaimedDialog(true);
          } else {
            setScannedData(scannedId);
            setScannedRole(role);
            setTransitioningToForm(true);
          }
        })
        .catch((error) => {
          console.error("Error checking coupon claim:", error);
          setScannedData(scannedId);
          setScannedRole(role);
          setTransitioningToForm(true);
        });
    } else {
      // Regular flow - open lead dialog
      setLeadDialog(true);
    }
  };

  // Handle manual ID entry
  const handleManualEntry = () => {
    if (!manualId.trim()) {
      setNotification({
        open: true,
        message: "Please enter a valid ID",
        severity: "error",
      });
      return;
    }

    if (!manualRole.trim()) {
      setNotification({
        open: true,
        message: "Please select a role",
        severity: "error",
      });
      return;
    }

    // Wait for auth data to load
    if (authLoading) {
      setNotification({
        open: true,
        message: "Please wait while we verify your authentication...",
        severity: "info",
      });
      return;
    }

    // Get the logged-in user's eventId
    const userEventId = authData?.eventId;

    if (!userEventId) {
      setNotification({
        open: true,
        message: "Unable to verify event. Please try logging in again.",
        severity: "error",
      });
      return;
    }

    // Check if manual entry is in QR code format (eventId,role,id)
    let scannedId = manualId.trim();
    let role = manualRole.trim();

    // Handle MECARD / VCARD manual paste: extract NOTE content if present
    let parsedManual = manualId.trim();
    const isCardFormat = /^MECARD:|^BEGIN:VCARD/i.test(parsedManual);
    if (isCardFormat) {
      const noteMatch = parsedManual.match(/NOTE:([^;]+)/i);
      if (noteMatch && noteMatch[1]) {
        parsedManual = noteMatch[1].trim();
      }
    }

    // If user entered a full QR code format, validate it strictly
    if (parsedManual.includes(",")) {
      // Check if it's a URL
      const isUrl = /^https?:\/\//i.test(parsedManual);
      if (isUrl) {
        setNotification({
          open: true,
          message:
            "Invalid QR code: This appears to be a URL, not a valid badge QR code.",
          severity: "error",
        });
        return;
      }

      const dataParts = parsedManual.split(",");

      // If it has commas, it MUST be exactly 3 parts (eventId,role,id)
      if (dataParts.length !== 3) {
        setNotification({
          open: true,
          message: `Invalid QR code format: Expected 3 parts separated by commas (eventId,role,id), but found ${dataParts.length} part(s).`,
          severity: "error",
        });
        return;
      }

      const scannedEventId = dataParts[0].trim();
      const qrRole = dataParts[1].trim();
      scannedId = dataParts[2].trim();

      // Validate that all parts are not empty
      if (!scannedEventId || !qrRole || !scannedId) {
        setNotification({
          open: true,
          message:
            "Invalid QR code: All parts (eventId, role, id) must be present and non-empty.",
          severity: "error",
        });
        return;
      }

      // CRITICAL: Validate eventId matches the logged-in user's eventId
      if (scannedEventId !== userEventId) {
        setNotification({
          open: true,
          message:
            "Invalid QR code: This QR code belongs to a different event.",
          severity: "error",
        });
        return;
      }

      // Use the role from QR code
      role = qrRole;
    }
    // If no commas, treat as manual ID entry (allow this for manual entry)

    setScannedData(scannedId);
    setScannedRole(role);
    setManualDialog(false);

    // Check if this is the coupon event
    if (userEventId === COUPON_CONFIG.EVENT_ID) {
      if (isCouponValidityExpired()) {
        setValidityExpiredDialog(true);
        return;
      }
      checkCouponClaimedQuery({
        variables: { id: scannedId, role },
      })
        .then((result) => {
          if (result.data?.checkCouponClaimed) {
            setAlreadyClaimedDialog(true);
          } else {
            setTransitioningToForm(true);
          }
        })
        .catch((error) => {
          console.error("Error checking coupon claim:", error);
          setTransitioningToForm(true);
        });
    } else {
      setLeadDialog(true);
    }
  };

  // Toggle camera facing mode
  const toggleCamera = () => {
    setFacingMode((prevMode) =>
      prevMode === "environment" ? "user" : "environment"
    );
  };

  // Reset scan state
  const resetScan = () => {
    setScanning(true);
    setScannedData(null);
    setScannedRole(null);
    setLeadDialog(false);
    setShowCouponForm(false);
    setTransitioningToForm(false);
    setAlreadyClaimedDialog(false);
    setValidityExpiredDialog(false);
    setLeadType("Hot Lead");
    setRemark("");
    setManualId("");
    setManualRole("");
  };

  // Handle coupon claim success
  const handleCouponSuccess = async (message) => {
    // Send email notification
    try {
      const userEventId = authData?.eventId;
      if (userEventId === COUPON_CONFIG.EVENT_ID && scannedData && scannedRole) {
        // Get coupon details from the form (we'll need to pass these)
        // For now, just show success message
        await sendCouponEmail({
          variables: {
            userId: scannedData,
            role: scannedRole,
            couponNo: "", // Will be set by backend
            totalBill: 0,
            discountApplied: 0,
            grandTotal: 0,
          },
        }).catch((err) => {
          console.error("Error sending coupon email:", err);
          // Don't fail the whole process if email fails
        });
      }
    } catch (error) {
      console.error("Error in coupon success handler:", error);
    }

    setNotification({
      open: true,
      message: message || "Coupon claimed successfully!",
      severity: "success",
    });

    setShowCouponForm(false);
    resetScan();
  };

  // Process and upload scan
  const processScan = async () => {
    try {
      if (!scannedRole) {
        setNotification({
          open: true,
          message:
            "Role information is missing. Please scan again or enter manually.",
          severity: "error",
        });
        return;
      }

      // First check if this is a duplicate
      const dupResult = await checkDuplicate({
        variables: {
          id: scannedData,
          role: scannedRole, // Use the extracted role
        },
      });

      if (dupResult.data.checkDuplicateId === "true") {
        setNotification({
          open: true,
          message: "This badge has already been scanned",
          severity: "warning",
        });
        return;
      }

      // Prepare input data
      const inputData = {
        type: leadType,
        remark: remark,
      };

      // Upload the scan result
      const result = await uploadScanResult({
        variables: {
          id: scannedData,
          role: scannedRole, // Use the extracted role
          inp: JSON.stringify(inputData),
        },
      });

      // Add to recent scans with current Unix timestamp
      const newScan = {
        id: scannedData,
        role: scannedRole,
        type: leadType,
        remark: remark,
        timestamp: new Date().toISOString(), // Store as ISO string for consistent handling
      };

      setRecentScans((prev) => [newScan, ...prev].slice(0, 5));

      // Show success notification
      setNotification({
        open: true,
        message: result.data.uploadScanResult,
        severity: "success",
      });

      // Reset for next scan
      resetScan();
    } catch (error) {
      console.error("Error scanning/uploading:", error);
      setNotification({
        open: true,
        message: error.message || "Failed to process scan",
        severity: "error",
      });
    }
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Grid container spacing={3}>
      <Grid
        item
        xs={12}
        md={8}
        lg={7}
        sx={{
          height: "auto",
          minHeight: isMobile ? "60vh" : "calc(100vh - 120px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ScannerContainer elevation={8}>
          <ScannerHeader>
            <Typography variant="h6" color="white">
              Badge Scanner
            </Typography>
            <Box>
              <IconButton
                color="inherit"
                onClick={toggleCamera}
                sx={{ color: "white" }}
              >
                <CameraswitchIcon />
              </IconButton>
            </Box>
          </ScannerHeader>

          <ScannerViewport>
            {showCouponForm || transitioningToForm ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography color="text.secondary">
                  {transitioningToForm ? "Preparing form..." : "Coupon claim form is open"}
                </Typography>
              </Box>
            ) : scanning ? (
              <>
                {hasPermission ? (
                  <QrReader
                    facingMode={facingMode}
                    delay={500}
                    onScan={(data) => {
                      if (data) handleScan({ text: data });
                    }}
                    onError={(err) => {
                      console.error("QR Scanner error:", err);
                      setNotification({
                        open: true,
                        message: "QR scanner error: " + err.message,
                        severity: "error",
                      });
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      objectFit: "cover",
                      maxWidth: "100%",
                      position: "relative",
                    }}
                  />
                ) : (
                  <CameraPermissionDenied setHasPermission={setHasPermission} />
                )}

                {hasPermission && (
                  <>
                    <ScannerOverlay />
                    <ScannerControls />
                  </>
                )}
              </>
            ) : scannedData ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "background.paper",
                  p: 3,
                }}
              >
                <Typography variant="h4" align="center">
                  Scanned ID: {scannedData}
                </Typography>
              </Box>
            ) : null}
          </ScannerViewport>

          <ScannerFooter>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => setManualDialog(true)}
                  startIcon={<AddIcon />}
                >
                  Manual Entry
                </Button>
              </Grid>
              <Grid item xs>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={resetScan}
                  disabled={scanning && !scannedData}
                >
                  {scanning ? "Cancel" : "New Scan"}
                </Button>
              </Grid>
            </Grid>
          </ScannerFooter>
        </ScannerContainer>
      </Grid>

      <Grid item xs={12} md={4} lg={5}>
        <Stack spacing={3}>
          {/* Recent Scans Card */}
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Scans
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {recentScans.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  No recent scans yet. Start scanning!
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {recentScans.map((scan, index) => (
                    <Paper variant="outlined" key={index} sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2">
                            ID: {scan.id}
                          </Typography>
                          {scan.role && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Role: {formatRoleForDisplay(scan.role)}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={scan.type}
                          size="small"
                          color={
                            scan.type === "Hot Lead"
                              ? "error"
                              : scan.type === "Warm Lead"
                              ? "warning"
                              : "info"
                          }
                        />
                      </Box>
                      {scan.remark && (
                        <Typography variant="body2" color="text.secondary">
                          {scan.remark}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 1 }}
                      >
                        {getTimeDifference(scan.timestamp)}
                        {" - "}
                        {(() => {
                          // Format date properly based on timestamp type
                          try {
                            let dateObj;
                            if (
                              typeof scan.timestamp === "number" ||
                              /^\d+$/.test(scan.timestamp)
                            ) {
                              dateObj = new Date(parseInt(scan.timestamp));
                            } else {
                              dateObj = new Date(scan.timestamp);
                            }

                            if (isNaN(dateObj.getTime())) {
                              return "Invalid date";
                            }

                            return dateObj.toLocaleString();
                          } catch (e) {
                            console.error("Error formatting date:", e);
                            return "Date error";
                          }
                        })()}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Instructions Card */}
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Scanner Instructions
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {authData?.eventId === "5087644490" ? (
                <>
                  <Typography variant="body2" paragraph>
                    1. Hold the coupon QR code steady in front of the camera.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    2. The scanner will automatically detect the code.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    3. Once scanned, enter bill details and upload the invoice to
                    claim the coupon.
                  </Typography>
                  <Typography variant="body2">
                    4. Use manual entry if the QR code is damaged or unreadable.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" paragraph>
                    1. Hold the badge QR code steady in front of the camera.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    2. The scanner will automatically detect the code.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    3. Once scanned, classify the lead type and add any remarks.
                  </Typography>
                  <Typography variant="body2">
                    4. Use manual entry if the QR code is damaged or unreadable.
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Grid>

      {/* Manual Entry Dialog */}
      <Dialog
        open={manualDialog}
        onClose={() => setManualDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Manual Badge Entry</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Badge ID"
            type="text"
            fullWidth
            variant="outlined"
            value={manualId}
            onChange={(e) => setManualId(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControl fullWidth margin="dense" variant="outlined">
            <InputLabel id="manual-role-label">Role</InputLabel>
            <Select
              labelId="manual-role-label"
              value={manualRole}
              label="Role"
              onChange={(e) => setManualRole(e.target.value)}
              disabled={loadingRoles}
              startAdornment={
                loadingRoles ? (
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                ) : null
              }
            >
              {loadingRoles ? (
                <MenuItem disabled>Loading roles...</MenuItem>
              ) : availableRoles.length === 0 ? (
                <MenuItem value="visitor">Visitor</MenuItem>
              ) : (
                availableRoles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {formatRoleForDisplay(role)}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDialog(false)}>Cancel</Button>
          <Button
            onClick={handleManualEntry}
            variant="contained"
            disabled={loadingRoles}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lead Details Dialog */}
      <Dialog open={leadDialog} onClose={resetScan} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            Lead Details
            <IconButton onClick={resetScan} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Full Scanned Data:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {originalData}
            </Typography>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  ID:
                </Typography>
                <Chip
                  label={scannedData || ""}
                  variant="outlined"
                  sx={{
                    fontSize: "1rem",
                    px: 1,
                    height: "auto",
                    "& .MuiChip-label": { px: 1, py: 0.5 },
                  }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Role:
                </Typography>
                <Chip
                  label={
                    scannedRole
                      ? formatRoleForDisplay(scannedRole)
                      : "Not specified"
                  }
                  color={scannedRole ? "primary" : "default"}
                  variant="outlined"
                  sx={{
                    fontSize: "1rem",
                    px: 1,
                    height: "auto",
                    "& .MuiChip-label": { px: 1, py: 0.5 },
                  }}
                />
              </Box>
            </Box>

            {!scannedRole && (
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel id="role-select-label">Select Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  value={scannedRole || ""}
                  label="Select Role"
                  onChange={(e) => setScannedRole(e.target.value)}
                  disabled={loadingRoles}
                  startAdornment={
                    loadingRoles ? (
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : null
                  }
                >
                  {loadingRoles ? (
                    <MenuItem disabled>Loading roles...</MenuItem>
                  ) : availableRoles.length === 0 ? (
                    <MenuItem value="visitor">Visitor</MenuItem>
                  ) : (
                    availableRoles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {formatRoleForDisplay(role)}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}
          </Box>

          <FormControl fullWidth margin="normal">
            <InputLabel id="lead-type-label">Lead Type</InputLabel>
            <Select
              labelId="lead-type-label"
              value={leadType}
              label="Lead Type"
              onChange={(e) => setLeadType(e.target.value)}
            >
              <MenuItem value="Hot Lead">Hot Lead</MenuItem>
              <MenuItem value="Warm Lead">Warm Lead</MenuItem>
              <MenuItem value="Cold Lead">Cold Lead</MenuItem>
            </Select>
          </FormControl>

          <TextField
            margin="normal"
            label="Remarks (Optional)"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={resetScan} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            onClick={processScan}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={uploadLoading || !scannedRole}
          >
            {uploadLoading ? "Saving..." : "Save Lead"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Coupon form as popup – QR reader unmounted first (transition), then dialog opens */}
      <CouponFormDialog
        open={showCouponForm}
        onClose={() => {
          setShowCouponForm(false);
          resetScan();
        }}
        scannedData={scannedData}
        scannedRole={scannedRole}
        onSuccess={(msg) => {
          setShowCouponForm(false);
          handleCouponSuccess(msg);
        }}
      />

      {/* Already Claimed Dialog */}
      <Dialog
        open={alreadyClaimedDialog}
        onClose={() => {
          setAlreadyClaimedDialog(false);
          resetScan();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Coupon Already Claimed</Typography>
            <IconButton onClick={() => {
              setAlreadyClaimedDialog(false);
              resetScan();
            }} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This coupon has already been claimed by this user.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Each user can only claim the coupon once across all shops/attendees.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAlreadyClaimedDialog(false);
              resetScan();
            }}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validity Expired Dialog */}
      <Dialog
        open={validityExpiredDialog}
        onClose={() => {
          setValidityExpiredDialog(false);
          resetScan();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Coupon Redemption Period Ended</Typography>
            <IconButton onClick={() => {
              setValidityExpiredDialog(false);
              resetScan();
            }} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            The coupon redemption validity period has expired. You can no longer claim this coupon.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            The offer was valid until {new Date(COUPON_CONFIG.VALIDITY_END_DATE).toLocaleDateString()}. Please check back for future promotions.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setValidityExpiredDialog(false);
              resetScan();
            }}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity || "info"}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default ScanningVisitorsPage;
